import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Layout } from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  History,
  Search,
  CheckCircle2,
  XCircle,
  RotateCcw,
  User,
  CreditCard,
  FileText,
  Send,
  Calendar,
  MessageSquare,
  ChevronUp,
  ChevronDown,
  Building2,
  Globe,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HistoriquePage = ({ user, settings }) => {
  const [historiqueData, setHistoriqueData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showReactivateModal, setShowReactivateModal] = useState(false);
  const [reactivating, setReactivating] = useState(false);
  const [sortField, setSortField] = useState("date_validation");
  const [sortDirection, setSortDirection] = useState("desc");

  useEffect(() => {
    fetchHistorique();
  }, []);

  const fetchHistorique = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/delais/historique`, {
        withCredentials: true,
      });
      setHistoriqueData(response.data);
    } catch (error) {
      console.error("Error fetching historique:", error);
      toast.error("Erreur lors du chargement de l'historique");
    } finally {
      setLoading(false);
    }
  };

  const handleReactivate = async () => {
    if (!selectedClient) return;
    
    setReactivating(true);
    try {
      await axios.post(
        `${API}/delais/reactivate/${selectedClient.client_id}`,
        {},
        { withCredentials: true }
      );
      toast.success("Client réactivé avec succès");
      setShowReactivateModal(false);
      setShowDetailModal(false);
      fetchHistorique();
    } catch (error) {
      console.error("Error reactivating client:", error);
      toast.error("Erreur lors de la réactivation");
    } finally {
      setReactivating(false);
    }
  };

  // Filter based on search
  const filteredData = searchTerm
    ? historiqueData.filter(
        (item) =>
          item.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.prenom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.code_client?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : historiqueData;

  // Sort data
  const sortedData = [...filteredData].sort((a, b) => {
    let aVal = a[sortField];
    let bVal = b[sortField];
    
    if (sortField === "nom") {
      aVal = `${a.nom} ${a.prenom}`.toLowerCase();
      bVal = `${b.nom} ${b.prenom}`.toLowerCase();
    }
    
    if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
    if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === "asc" ? (
      <ChevronUp className="h-4 w-4" />
    ) : (
      <ChevronDown className="h-4 w-4" />
    );
  };

  const StatusBadge = ({ status, label }) => (
    <div className="flex items-center gap-1">
      {status ? (
        <CheckCircle2 className="h-4 w-4 text-green-600" />
      ) : (
        <XCircle className="h-4 w-4 text-red-600" />
      )}
      <span className={`text-xs ${status ? "text-green-600" : "text-red-600"}`}>
        {label || (status ? "OUI" : "NON")}
      </span>
    </div>
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return format(new Date(dateStr), "dd/MM/yyyy HH:mm", { locale: fr });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <Layout user={user} settings={settings}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} settings={settings}>
      <div className="space-y-6" data-testid="historique-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground flex items-center gap-2">
            <History className="h-6 w-6 text-primary" />
            Historique des dossiers validés
          </h1>
          <p className="text-muted-foreground mt-1">
            Consultez les dossiers marqués comme "en règle"
          </p>
        </div>

        {/* Search */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Rechercher par nom, prénom ou code client..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
              <Badge variant="secondary" className="text-sm">
                {sortedData.length} dossier{sortedData.length !== 1 ? "s" : ""} validé{sortedData.length !== 1 ? "s" : ""}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardHeader>
            <CardTitle>Dossiers en règle</CardTitle>
            <CardDescription>
              Cliquez sur un dossier pour voir les détails ou le réactiver
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {sortedData.length === 0 ? (
              <div className="text-center py-12">
                <History className="h-12 w-12 text-muted-foreground/50 mx-auto mb-3" />
                <p className="text-muted-foreground">
                  {searchTerm ? "Aucun dossier trouvé" : "Aucun dossier validé dans l'historique"}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead 
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => toggleSort("code_client")}
                      >
                        <div className="flex items-center gap-1">
                          Code <SortIcon field="code_client" />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => toggleSort("nom")}
                      >
                        <div className="flex items-center gap-1">
                          Client <SortIcon field="nom" />
                        </div>
                      </TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Paiement</TableHead>
                      <TableHead>Signature</TableHead>
                      <TableHead>TED</TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => toggleSort("date_validation")}
                      >
                        <div className="flex items-center gap-1">
                          Date validation <SortIcon field="date_validation" />
                        </div>
                      </TableHead>
                      <TableHead>Validé par</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedData.map((item) => (
                      <TableRow
                        key={item.client_id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => {
                          setSelectedClient(item);
                          setShowDetailModal(true);
                        }}
                        data-testid={`historique-row-${item.code_client}`}
                      >
                        <TableCell className="font-mono text-sm font-medium text-primary">
                          {item.code_client}
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{item.prenom} {item.nom}</p>
                            <p className="text-xs text-muted-foreground">{item.ville}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge 
                            variant="outline" 
                            className={item.type_traitement === "En ligne" 
                              ? "border-blue-300 text-blue-700 bg-blue-50" 
                              : "border-orange-300 text-orange-700 bg-orange-50"
                            }
                          >
                            {item.type_traitement === "En ligne" ? (
                              <Globe className="h-3 w-3 mr-1" />
                            ) : (
                              <Building2 className="h-3 w-3 mr-1" />
                            )}
                            {item.type_traitement}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {item.preparation_gratuite ? (
                            <Badge variant="secondary" className="bg-green-100 text-green-700">
                              Gratuit
                            </Badge>
                          ) : (
                            <StatusBadge status={item.payment_complete} />
                          )}
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={item.principal_signed} />
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={item.principal_transmitted} />
                        </TableCell>
                        <TableCell className="text-sm">
                          {formatDate(item.date_validation)}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {item.valide_par || "-"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Detail Modal */}
        <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                Détails du dossier validé
              </DialogTitle>
              <DialogDescription>
                {selectedClient?.prenom} {selectedClient?.nom} ({selectedClient?.code_client})
              </DialogDescription>
            </DialogHeader>

            {selectedClient && (
              <div className="space-y-6">
                {/* Client Info */}
                <div className="grid grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
                  <div>
                    <p className="text-sm text-muted-foreground">Client</p>
                    <p className="font-medium">{selectedClient.prenom} {selectedClient.nom}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Ville</p>
                    <p className="font-medium">{selectedClient.ville}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Situation</p>
                    <p className="font-medium">{selectedClient.situation_familiale}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Type de traitement</p>
                    <p className="font-medium">{selectedClient.type_traitement}</p>
                  </div>
                </div>

                {/* Status Summary */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 border rounded-lg text-center">
                    <CreditCard className="h-5 w-5 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">Paiement</p>
                    {selectedClient.preparation_gratuite ? (
                      <Badge className="mt-1 bg-green-100 text-green-700">Gratuit</Badge>
                    ) : (
                      <StatusBadge status={selectedClient.payment_complete} />
                    )}
                  </div>
                  <div className="p-4 border rounded-lg text-center">
                    <FileText className="h-5 w-5 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">Signature</p>
                    <StatusBadge status={selectedClient.principal_signed} />
                  </div>
                  <div className="p-4 border rounded-lg text-center">
                    <Send className="h-5 w-5 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">TED</p>
                    <StatusBadge status={selectedClient.principal_transmitted} />
                  </div>
                </div>

                {/* Validation Info */}
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <h4 className="font-semibold text-green-800 flex items-center gap-2 mb-3">
                    <CheckCircle2 className="h-4 w-4" />
                    Informations de validation
                  </h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-green-700">Date de validation</p>
                      <p className="font-medium text-green-900">{formatDate(selectedClient.date_validation)}</p>
                    </div>
                    <div>
                      <p className="text-green-700">Validé par</p>
                      <p className="font-medium text-green-900">{selectedClient.valide_par || "-"}</p>
                    </div>
                  </div>
                  {selectedClient.commentaire_validation && (
                    <div className="mt-3 pt-3 border-t border-green-200">
                      <p className="text-green-700 flex items-center gap-1">
                        <MessageSquare className="h-4 w-4" />
                        Commentaire de validation
                      </p>
                      <p className="font-medium text-green-900 mt-1">{selectedClient.commentaire_validation}</p>
                    </div>
                  )}
                </div>

                {/* Remarks */}
                {selectedClient.remarques && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-blue-700 flex items-center gap-1 text-sm">
                      <MessageSquare className="h-4 w-4" />
                      Remarques du dossier
                    </p>
                    <p className="font-medium text-blue-900 mt-1">{selectedClient.remarques}</p>
                  </div>
                )}
              </div>
            )}

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setShowDetailModal(false)}>
                Fermer
              </Button>
              <Button
                variant="default"
                className="bg-orange-600 hover:bg-orange-700"
                onClick={() => setShowReactivateModal(true)}
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Réactiver le dossier
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reactivate Confirmation Modal */}
        <Dialog open={showReactivateModal} onOpenChange={setShowReactivateModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-orange-600">
                <RotateCcw className="h-5 w-5" />
                Confirmer la réactivation
              </DialogTitle>
              <DialogDescription>
                Voulez-vous vraiment réactiver ce dossier ?
              </DialogDescription>
            </DialogHeader>

            <div className="py-4">
              <p className="text-sm text-muted-foreground">
                Le dossier de <strong>{selectedClient?.prenom} {selectedClient?.nom}</strong> sera 
                remis dans la liste de suivi des délais active.
              </p>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowReactivateModal(false)} disabled={reactivating}>
                Annuler
              </Button>
              <Button
                className="bg-orange-600 hover:bg-orange-700"
                onClick={handleReactivate}
                disabled={reactivating}
              >
                {reactivating ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
                    Réactivation...
                  </>
                ) : (
                  <>
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Confirmer
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default HistoriquePage;
